import { Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageComponent } from '@unity/abstract/abstract-manage.component';
import { ProgramDetailedDto, ProgramsClient, ProgramUpdateDto, TransitionRequestDto, UnityWorkflowState } from '@unity/clients';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { FormLayoutComponent } from '@unity/layouts/form-layout/form-layout.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ActionDeleteSharedComponent } from '@unity/shared/composed/action-delete-shared/action-delete-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { PanelManageSharedComponent } from '@unity/shared/composed/panel-manage-shared/panel-manage-shared.component';
import { BannerAlertSharedComponent } from '@unity/shared/presentation/banner-alert-shared/banner-alert-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityEntitlementPath } from '@unity/utils/types';

class ProgramUpdateSchema implements FormGroupControlSchema<ProgramUpdateDto> {
  public code: FormControl<string>;
  public name: FormControl<string>;

  public constructor(program?: ProgramDetailedDto) {
    this.code = stringFormControl({ disabled: true, value: program?.code });
    this.name = stringFormControl({ disabled: true, value: program?.name });
  }
}

@Component({
  selector: 'unity-manage-program-page',
  standalone: true,
  imports: [
    KENDO_STACKLAYOUT,
    ReactiveFormsModule,
    PageContainerComponent,
    TextboxControlComponent,
    FormLayoutComponent,
    BannerAlertSharedComponent,
    PanelManageSharedComponent,
    ActionDeleteSharedComponent,
    AuthorizeDirective,
    WorkflowSelectorSharedComponent,
    ActionSharedComponent,
  ],
  templateUrl: './manage-program-page.component.html',
  styleUrl: './manage-program-page.component.scss',
})
export class ManageProgramPageComponent extends AbstractManageComponent<ProgramDetailedDto, ProgramUpdateDto, ProgramUpdateSchema> {
  private readonly programsClient = inject(ProgramsClient);

  public readonly $programId = input.required({ alias: 'programId', transform: (value: string) => parseInt(value) });

  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      return resourceId ? { resourceId } : undefined;
    },
    stream: ({ params: { resourceId } }) => this.programsClient.getOneAsDetailed(resourceId),
  });

  protected override updateResourceFn$ = (resourceId: number, form: FormGroup<ProgramUpdateSchema>) =>
    this.programsClient.updateOne(resourceId, form.getRawValue());

  protected override deleteResourceFn$ = (resourceId: number) => this.programsClient.deleteOne(resourceId);

  protected override availableTransitionsFn$ = (resourceId: number) => this.availableTransitionsClient.getAllForProgramAsMini(resourceId);

  protected override doTransitionFn$ = (resourceId: number, transition: TransitionRequestDto) =>
    this.transitionsClient.setForProgram(resourceId, transition);

  protected override newControlsFn(dto?: ProgramDetailedDto) {
    return new ProgramUpdateSchema(dto);
  }

  protected override editEntitlement: UnityEntitlementPath = 'Program:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'Program:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'Program:Delete';
  protected override resourceName = 'Program';
  protected override $resourceId = this.$programId;
  protected override readonly $contentHeader = computed(() => `Program: ${this.$resource()?.name}`);

  protected readonly $isDisabled = computed(() => this.$resource()?.current_state.enum_value == UnityWorkflowState.Disabled);

  protected returnToDashboardAction = QuickActions.ReturnToDashboard;
}
